import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const EmailChip = styled(Chip)(({ theme }) => ({
  margin: '2px',
  '& .MuiChip-deleteIcon': {
    color: theme.palette.error.main,
  },
}));

const STEPS = ['L1 Approval', 'L2 Approval', 'Intimation'];

const EmailModalProductFinancing = ({ open, onClose, onSubmit, pfTrainerConfig }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [emailData, setEmailData] = useState({
    stage1: { cc: [] },
    stage2: { cc: [] },
    stage3: { to: [], cc: [] },
  });
  const [toInput, setToInput] = useState('');
  const [ccInput, setCcInput] = useState('');

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setEmailData({ stage1: { cc: [] }, stage2: { cc: [] }, stage3: { to: [], cc: [] } });
      setToInput('');
      setCcInput('');
    }
  }, [open]);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const addEmails = (stage, field, raw, clearFn) => {
    const emails = raw
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e && isValidEmail(e));
    if (emails.length === 0) return;
    setEmailData((prev) => ({
      ...prev,
      [stage]: { ...prev[stage], [field]: [...(prev[stage][field] || []), ...emails] },
    }));
    clearFn('');
  };

  const handleKeyDown = (stage, field, e, value, setFn) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      addEmails(stage, field, value, setFn);
    }
  };

  const handlePaste = (stage, field, e, setFn) => {
    e.preventDefault();
    addEmails(stage, field, e.clipboardData?.getData('text') || '', setFn);
  };

  const removeEmail = (stage, field, email) => {
    setEmailData((prev) => ({
      ...prev,
      [stage]: { ...prev[stage], [field]: prev[stage][field].filter((e) => e !== email) },
    }));
  };

  const handleNext = () => {
    if (activeStep < 2) {
      setActiveStep((s) => s + 1);
      setToInput('');
      setCcInput('');
    } else {
      onSubmit({
        stage1: { to: [], cc: emailData.stage1.cc },
        stage2: { to: [], cc: emailData.stage2.cc },
        stage3: { to: emailData.stage3.to, cc: emailData.stage3.cc },
      });
      onClose();
    }
  };

  const handleBack = () => {
    setActiveStep((s) => s - 1);
    setToInput('');
    setCcInput('');
  };

  const canProceed = () => {
    if (activeStep === 2) return emailData.stage3.to.length > 0;
    return true;
  };

  const getApprovers = () => {
    if (activeStep === 0) return pfTrainerConfig?.l1Approvers || [];
    if (activeStep === 1) return pfTrainerConfig?.l2Approvers || [];
    return [];
  };

  const stage = `stage${activeStep + 1}`;
  const approvers = getApprovers();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Product Financing — Email Configuration</DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep < 2 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              {activeStep === 0 ? 'L1' : 'L2'} approvers are pre-configured in Training
              Configuration and will receive the approval email automatically. You can optionally
              add CC recipients.
            </Alert>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Approvers (auto-configured)
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {approvers.length > 0 ? (
                  approvers.map((a, i) => (
                    <Chip
                      key={i}
                      label={`${a.empName} (${a.empId})`}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No approvers configured
                  </Typography>
                )}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                CC Emails
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                {(emailData[stage]?.cc || []).map((email, i) => (
                  <EmailChip
                    key={i}
                    label={email}
                    onDelete={() => removeEmail(stage, 'cc', email)}
                  />
                ))}
              </Stack>
              <TextField
                fullWidth
                label="Add CC Email"
                value={ccInput}
                onChange={(e) => setCcInput(e.target.value)}
                onKeyDown={(e) => handleKeyDown(stage, 'cc', e, ccInput, setCcInput)}
                onPaste={(e) => handlePaste(stage, 'cc', e, setCcInput)}
                margin="normal"
                type="email"
                helperText="Press space, enter, or paste comma-separated emails"
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">Intimation Email Details</Typography>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                To Emails
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                {emailData.stage3.to.map((email, i) => (
                  <EmailChip
                    key={i}
                    label={email}
                    onDelete={() => removeEmail('stage3', 'to', email)}
                  />
                ))}
              </Stack>
              <TextField
                fullWidth
                label="Add To Email"
                value={toInput}
                onChange={(e) => setToInput(e.target.value)}
                onKeyDown={(e) => handleKeyDown('stage3', 'to', e, toInput, setToInput)}
                onPaste={(e) => handlePaste('stage3', 'to', e, setToInput)}
                margin="normal"
                type="email"
                helperText="Press space, enter, or paste comma-separated emails"
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                CC Emails
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                {emailData.stage3.cc.map((email, i) => (
                  <EmailChip
                    key={i}
                    label={email}
                    onDelete={() => removeEmail('stage3', 'cc', email)}
                  />
                ))}
              </Stack>
              <TextField
                fullWidth
                label="Add CC Email"
                value={ccInput}
                onChange={(e) => setCcInput(e.target.value)}
                onKeyDown={(e) => handleKeyDown('stage3', 'cc', e, ccInput, setCcInput)}
                onPaste={(e) => handlePaste('stage3', 'cc', e, setCcInput)}
                margin="normal"
                type="email"
                helperText="Press space, enter, or paste comma-separated emails"
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleBack} disabled={activeStep === 0}>
          Back
        </Button>
        <Button onClick={handleNext} variant="contained" disabled={!canProceed()}>
          {activeStep === 2 ? 'Finish' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailModalProductFinancing;
